import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { takeUntil } from 'rxjs/operators';

import { AppClientContext } from 'src/app-client/_appCommon/base/AppClientContext';
import { CustomerDataService } from 'src/app-client/_dataservices/CustomerDataService';
import { CustomerLevelDataService } from 'src/app-client/_dataservices/CustomerLevelDataService';
import { GlobalConstants } from 'src/app/_appCommon/base/AppInterfaceAndBaseClasses';
import { ListControllerBase } from 'src/app-client/_appCommon/base/ListControllerBase';
import { ProductDataService } from 'src/app-client/_dataservices/ProductDataService';
import { Product, ProductType, ProductTypeTooltipText } from 'src/app-client/_appCommon/base/AppInterfaceAndBaseClasses';
import { ShoppingCart } from 'src/app/shopping-cart';
import { UrlQueryDef } from 'src/app-client/_appCommon/base/UrlQueryDef';
import { IVA } from 'src/app-client/_appCommon/base/Constants';

@Component({
  selector: 'app-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.css'],
  providers: [CustomerDataService, CustomerLevelDataService, CustomerLevelDataService]
})
export class ProductListComponent extends ListControllerBase<ProductDataService>
{

  public currentCustomer: any;
  public customerTypeIdNotFound: boolean = false;
  public dataSource: Array<Product> = [];
  public defaultImage = GlobalConstants.altProductImagePath;
  public hasShoppingDiscount: boolean = false;
  public nanoDescription = ProductTypeTooltipText.get(ProductType.Nano);
  public shoppingDiscount: any;
  public simpleDescription = ProductTypeTooltipText.get(ProductType.Simple);
  public socialDescription = ProductTypeTooltipText.get(ProductType.Social);
  public unsubscribe: Subject<boolean> = new Subject();

  constructor(
    title: Title,
    private customerDataService: CustomerDataService,
    private customerLevelDataService: CustomerLevelDataService,
    private router: Router,
    private shoppingCart: ShoppingCart,
    protected productDataService: ProductDataService
  )
  {
    super("Productos", title, productDataService);
    this.productDataService.productListCategoryIdChanged.pipe(takeUntil(this.unsubscribe)).subscribe(id =>
    {
      this.getCustomerInfo();
    });
  }

  ngAfterViewInit()
  {
    this.getCustomerInfo();
  }

  ngOnInit()
  {

  }

  ngOnDestroy(): void
  {
    this.unsubscribe.next(true);
    this.unsubscribe.unsubscribe();
  }

  // gets the current customer information
  public getCustomerInfo()
  {
    if (AppClientContext.Instance.user)
    {
      this.customerDataService.getByUserId(AppClientContext.Instance.user.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe((response: any) =>
        {
          this.currentCustomer = response.items.firstOrNull();
          if (this.currentCustomer)
          {
            this.getShoppingDiscount();
            AppClientContext.Instance.customerId = this.currentCustomer.id;

            if (this.currentCustomer.customerTypeId === null)
            {
              this.customerTypeIdNotFound = true;
            }
            else
            {
              this.getAllProducts();
            }
          }
          else
          {
            this.router.navigate(["/ecommerce"]);
          }
        });
    }
    else
    {
      this.router.navigate(["/ecommerce"]);
    }
  }

  showDetails(productElement)
  {
    this.router.navigate(["ecommerce/tienda/detalle", productElement.id]);
  }

  private getShoppingDiscount(): void
  {
    let id = this.currentCustomer.customerLevelId;
    if (id != null)
    {
      this.customerLevelDataService.getCustomerLevelById(id).subscribe((response: any) =>
      {
        if (parseFloat(response.shoppingDiscount) != 0 && !response.isDeleted)
        {
          this.shoppingDiscount = parseFloat(response.shoppingDiscount);
          this.hasShoppingDiscount = true;
        }
      }, (fail: any) =>
      {
        this.setDangerAlert(fail.message);
      });
    }
  }

  // get all the products
  private getProductStock()
  {
    this.productDataService.getProductStock(this.getQueryDefForGetAll()).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) =>
      {
        this.dataSource = res;
        this.onAfterGetAll();
      }, (error) =>
      {
        this.setDangerAlert(error.message);
      });
  }

  // filter the products by "category-id"
  private getProductStockFilteredByCategoryId()
  {
    this.productDataService.getStockFilteredByCategoryId(this.getQueryDefForGetByCategoryId()).pipe(takeUntil(this.unsubscribe))
      .subscribe((res) =>
      {
        this.dataSource = res;
        this.onAfterGetAll();
      }, (error) =>
      {
        this.setDangerAlert(error.message);
      });
  }

  // gets all the products
  public getAllProducts()
  {
    if (this.productDataService.productListCategoryId === -1)
    {
      // gets all the products not filtered
      this.getProductStock();
    }
    else
    {
      // gets all the products filtered by "category-id"
      this.getProductStockFilteredByCategoryId();
    }
  }

  private getProductPrices(productList: Product[]): void
  {
    productList.forEach(product =>
    {
      let productPriceListByCustomerId = product.priceList.find(x => x.customerTypeId == this.currentCustomer.customerTypeId && !x.isDeleted);
      let priceProduct = 0;
      let tax = 0;
      let totalTax = 0;
      let subtotal = 0;
      let discount = 0;

      if (productPriceListByCustomerId)
      {
        priceProduct = productPriceListByCustomerId.priceProduct;
        tax = productPriceListByCustomerId.tax;
        subtotal = tax != 0 ? priceProduct : priceProduct / (1 + IVA);
        discount = this.shoppingDiscount != 0 ? subtotal * this.shoppingDiscount : 0;
        subtotal -= discount;
        totalTax = subtotal * (tax != 0 ? tax : IVA);
        product.itemPrices =
        {
          price: priceProduct,
          subtotal: subtotal,
          priceExtraAligner: productPriceListByCustomerId.priceExtraAligner,
          priceExtraDentalMonitoring: productPriceListByCustomerId.priceExtraDentalMonitoring,
          priceComplexCase: productPriceListByCustomerId.priceComplexCase,
          tax: tax,
          totalTax: totalTax,
          shoppingDiscount: this.shoppingDiscount != 0 ? this.shoppingDiscount : null,
          priceWithDiscount: subtotal + (tax != 0 ? 0 : totalTax),
        };
      }
      else
      {
        priceProduct = product.basePriceProduct;
        tax = product.baseTax;
        subtotal = tax != 0 ? priceProduct : priceProduct / (1 + IVA);
        discount = this.shoppingDiscount != 0 ? subtotal * this.shoppingDiscount : 0;
        subtotal -= discount;
        totalTax = subtotal * (tax != 0 ? tax : IVA);
        product.itemPrices =
        {
          price: priceProduct,
          subtotal: subtotal,
          priceExtraAligner: product.basePriceExtraAligner,
          priceExtraDentalMonitoring: product.basePriceDentalMonitoring,
          priceComplexCase: product.basePriceComplexCase,
          tax: tax,
          totalTax: totalTax,
          shoppingDiscount: this.shoppingDiscount != 0 ? this.shoppingDiscount : null,
          priceWithDiscount: subtotal + (tax != 0 ? 0 : totalTax),
        };
      }
    });
  }

  public getProductImages(productList: Product[]): void
  {
    productList.forEach(product =>
    {
      product.principalImage = product.images.length > 0 ? product.images[0].url : this.defaultImage;
    });
  }

  public getTooltipDescription(productName: string): string
  {
    if(this.isNano(productName))
      return this.nanoDescription;

    if(this.isSocial(productName))
      return this.socialDescription;

    if(this.isSimple(productName))
      return this.simpleDescription;
  }

  // gets the type of the product by "product name"
  public hasTooltip(productName: string): boolean
  {
    return productName.toLowerCase() === "nano"
           || productName.toLowerCase() === "social"
           || productName.toLowerCase() === "simple";
  }

  // gets the type of the product by "product name"
  // it matches with "nano"
  public isNano(productName: string): boolean
  {
    return productName.toLowerCase() === "nano";
  }

  // gets the type of the product by "product name"
  // it matches with "social"
  public isSocial(productName: string): boolean
  {
    return productName.toLowerCase() === "social";
  }

  // gets the type of the product by "product name"
  // it matches with "simple"
  public isSimple(productName: string): boolean
  {
    return productName.toLowerCase() === "simple";
  }

  // gets the url-query-definition for getting all the products
  protected getQueryDefForGetAll(): UrlQueryDef
  {
    let queryDef: UrlQueryDef = new UrlQueryDef();
    queryDef.addParam("GetAll", true);
    queryDef.addParam("CustomerTypeId", this.currentCustomer.customerTypeId);
    queryDef.includes = "ProductCustomerTypeList";
    return queryDef;
  }

  // gets the url-query-definition for getting filtered products by "category-id"
  protected getQueryDefForGetByCategoryId(): UrlQueryDef
  {
    let queryDef: UrlQueryDef = new UrlQueryDef();
    queryDef.addParam("CustomerTypeId", this.currentCustomer.customerTypeId);
    queryDef.addParam("CategoryId", this.productDataService.productListCategoryId);
    return queryDef;
  }

  protected onAfterGetAll(): void
  {
    this.getProductImages(this.dataSource);
    this.getProductPrices(this.dataSource);
  }
}
